import { useRef } from 'react'

import { DeviceI } from '../types/types'

export type PlacementActionType = 'move_placement' | 'place_device'

interface QueuedPlacementI {
  data: DeviceI
  action: PlacementActionType
}

interface UsePlacementWriteQueueIProps {
  /** Persists one placement. Rejections are reported through `onWriteError`. */
  write: (data: DeviceI) => Promise<boolean>
  onWriteError: (
    error: unknown,
    deviceId: string,
    action: PlacementActionType
  ) => void
  /** Devices whose placement is being removed — nothing more should be written. */
  shouldSkipDevice: (deviceId: string) => boolean
}

export interface UsePlacementWriteQueueResultIProps {
  enqueue: (data: DeviceI, action: PlacementActionType) => Promise<boolean>
  cancelQueued: (deviceId: string) => void
  /** Writes a placement left queued while the device was skipped, once it no longer is. */
  resume: (deviceId: string) => void
  trackExternalWrite: (deviceIds: string[], request: Promise<unknown>) => void
  /** Outstanding writes for a device, so a caller can await them only when there are any. */
  getPendingWrites: (deviceId: string) => Promise<unknown>[]
  /** Devices with a write queued or in flight — the canvas is ahead of the server. */
  getPendingDeviceIds: () => string[]
}

/**
 * Serializes placement writes per device.
 *
 * Two nudges in quick succession used to put two mutations in flight for one device
 * with nothing ordering them: whichever response landed last decided the coordinates
 * in the normalized cache, and the backend applied them in its own order too, so the
 * position the user settled on could lose to the one before it. One write at a time
 * removes both races, and only the newest of whatever piles up behind it is sent —
 * a position already dragged away from does not need a round trip of its own.
 *
 * Writes issued outside the queue (`trackExternalWrite`) are waited on too, so a
 * device placed by a bulk write is not written again until that write has landed.
 *
 * Callers also need to know which devices have writes outstanding: their placement
 * exists only on the canvas until the write lands, so a query result arriving in the
 * meantime must not reconcile it away.
 */
export const usePlacementWriteQueue = ({
  write,
  onWriteError,
  shouldSkipDevice,
}: UsePlacementWriteQueueIProps): UsePlacementWriteQueueResultIProps => {
  /** Newest placement per device, waiting for the one before it to finish writing. */
  const queuedRef = useRef(new Map<string, QueuedPlacementI>())
  /** The drain loop running for a device, if there is one. */
  const activeRef = useRef(new Map<string, Promise<boolean>>())
  /** Writes issued elsewhere (bulk companion placement) that still count as pending. */
  const externalRef = useRef(new Map<string, Promise<unknown>>())

  const drain = async (deviceId: string): Promise<boolean> => {
    let didPersist = false
    // Let a bulk companion write land first so the two can't race. Its caller reports
    // failures. Await only if present — awaiting nothing still defers the first write.
    const external = externalRef.current.get(deviceId)
    if (external) await external.catch(() => undefined)

    // Re-read after every write: a newer placement can be queued while one is in
    // flight, which is the whole reason the queue exists.
    let queued = queuedRef.current.get(deviceId)

    while (queued && !shouldSkipDevice(deviceId)) {
      queuedRef.current.delete(deviceId)
      didPersist = await write(queued.data).catch((error) => {
        onWriteError(error, deviceId, queued.action)
        return false
      })
      queued = queuedRef.current.get(deviceId)
    }

    return didPersist
  }

  const enqueue = (
    data: DeviceI,
    action: PlacementActionType
  ): Promise<boolean> => {
    if (shouldSkipDevice(data.id)) return Promise.resolve(false)

    queuedRef.current.set(data.id, { data, action })

    return startDrain(data.id)
  }

  const startDrain = (deviceId: string): Promise<boolean> => {
    const active = activeRef.current.get(deviceId)
    if (active) return active

    const chain = drain(deviceId)
    activeRef.current.set(deviceId, chain)
    void chain.finally(() => activeRef.current.delete(deviceId))

    return chain
  }

  const cancelQueued = (deviceId: string) => {
    queuedRef.current.delete(deviceId)
  }

  // A skip stops the drain with the newest placement still queued, and nothing else
  // would ever send it — the canvas would hold a position the server never received.
  const resume = (deviceId: string) => {
    if (!queuedRef.current.has(deviceId) || shouldSkipDevice(deviceId)) return
    void startDrain(deviceId)
  }

  const trackExternalWrite = (
    deviceIds: string[],
    request: Promise<unknown>
  ) => {
    deviceIds.forEach((deviceId) => externalRef.current.set(deviceId, request))
    void request
      .finally(() =>
        deviceIds.forEach((deviceId) => {
          // A later write for the same device supersedes this entry; leave it alone.
          if (externalRef.current.get(deviceId) === request) {
            externalRef.current.delete(deviceId)
          }
        })
      )
      // `finally` re-raises a rejection; the request's caller already reports it.
      .catch(() => undefined)
  }

  const getPendingWrites = (deviceId: string) =>
    [activeRef.current.get(deviceId), externalRef.current.get(deviceId)].filter(
      Boolean
    )

  const getPendingDeviceIds = () => [
    ...queuedRef.current.keys(),
    ...activeRef.current.keys(),
    ...externalRef.current.keys(),
  ]

  return {
    enqueue,
    cancelQueued,
    resume,
    trackExternalWrite,
    getPendingWrites,
    getPendingDeviceIds,
  }
}
